import { useMemo } from 'react';

/**
 * Custom hook to calculate running balances, total balance, 
 * total debits, and total credits for a set of ledger entries.
 * * @param {Array} rawEntries - The raw array of ledger transaction objects
 * @returns {Object} { totalBalance, totalDebit, totalCredit, ledgerEntries }
 */
export const useLedger = (rawEntries = []) => {
  return useMemo(() => {
    // 1. Sort chronologically (oldest first) to calculate correct running balances
    const chronologicalEntries = [...rawEntries].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );

    let currentBalance = 0;
    let accumulatedDebit = 0;
    let accumulatedCredit = 0;

    // 2. Loop forward to calculate running balances and track absolute totals
    const computedEntries = chronologicalEntries.map((entry) => {
      const debit = parseFloat(entry.debit) || 0;
      const credit = parseFloat(entry.credit) || 0;

      // Track absolute sums
      accumulatedDebit += debit;
      accumulatedCredit += credit;

      // Standard accounting logic: Credit increases balance, Debit decreases it
      currentBalance = currentBalance + credit - debit;

      return {
        ...entry,
        debit,
        credit,
        runningBalance: currentBalance,
      };
    });

    // The final calculated balance is our total balance
    const totalBalance = currentBalance;

    // 3. Reverse the array so the newest transaction is placed first for display
    const ledgerEntries = computedEntries.reverse();

    return {
      totalBalance,
      totalDebit: accumulatedDebit,
      totalCredit: accumulatedCredit,
      ledgerEntries,
    };
  }, [rawEntries]);
};